import { Field, FieldRow } from '../../../forms/fields';
import { DividendYieldsEditor } from '../fields/DividendYieldsEditor';
import { GlidePathsEditor } from '../fields/GlidePathsEditor';
import { OrderEditor } from '../fields/OrderEditor';
import type { SectionProps } from '../sectionProps';

/**
 * STRATEGY & horizon section — the plan-level controls that aren't per-row:
 *  - contribution / withdrawal ORDER over the plan's accounts (reorderable, via OrderEditor);
 *  - `rebalanceBand` drift tolerance;
 *  - the plan-level `startYear`, `horizonYears`, optional `inflation`;
 *  - the `glidePaths` editor (named allocation schedules referenced by accounts);
 *  - the `assumptions.dividendYields` per-asset-class defaults.
 *
 * Numeric fields register with `valueAsNumber`; the order editors and the record editors use a
 * `Controller` internally so the whole list / map updates atomically. All validation is the shared
 * `PlanSchema`, surfaced inline.
 */
export function StrategySection({ form }: SectionProps): React.ReactElement {
  const { register, watch, formState } = form;
  const errors = formState.errors;
  const accounts = watch('accounts').map((a) => ({ id: a.id, name: a.name }));

  return (
    <section>
      <h2>Strategy &amp; horizon</h2>
      <p style={{ color: 'var(--ink-muted)', marginBottom: 'var(--sp-6)' }}>
        How surplus is invested and how shortfalls are funded, plus the projection window.
      </p>

      <div className="card" style={{ marginBottom: 'var(--sp-4)' }}>
        <h3 className="card__title">Projection window</h3>
        <FieldRow>
          <div style={{ flex: 1, minWidth: 130 }}>
            <Field label="Start year" error={errors.startYear} htmlFor="startYear">
              <input
                id="startYear"
                className="input input--num"
                type="number"
                {...register('startYear', { valueAsNumber: true })}
              />
            </Field>
          </div>
          <div style={{ flex: 1, minWidth: 130 }}>
            <Field label="Horizon (years)" error={errors.horizonYears} htmlFor="horizonYears">
              <input
                id="horizonYears"
                className="input input--num"
                type="number"
                {...register('horizonYears', { valueAsNumber: true })}
              />
            </Field>
          </div>
          <div style={{ flex: 1, minWidth: 130 }}>
            <Field
              label="Inflation"
              error={errors.inflation}
              htmlFor="inflation"
              hint="Decimal, e.g. 0.025."
            >
              <input
                id="inflation"
                className="input input--num"
                type="number"
                step="0.005"
                {...register('inflation', { setValueAs: emptyToUndefinedNumber })}
              />
            </Field>
          </div>
        </FieldRow>
      </div>

      <div className="card" style={{ marginBottom: 'var(--sp-4)' }}>
        <h3 className="card__title">Contribution &amp; withdrawal order</h3>
        <FieldRow>
          <div style={{ flex: 1, minWidth: 260 }}>
            <OrderEditor
              form={form}
              name="strategy.contributionOrder"
              label="Contribution order"
              accounts={accounts}
              hint="Surplus funds these accounts in order."
            />
          </div>
          <div style={{ flex: 1, minWidth: 260 }}>
            <OrderEditor
              form={form}
              name="strategy.withdrawalOrder"
              label="Withdrawal order"
              accounts={accounts}
              hint="Shortfalls draw from these in order."
            />
          </div>
        </FieldRow>
        <div style={{ maxWidth: 220 }}>
          <Field
            label="Rebalance band"
            error={errors.strategy?.rebalanceBand}
            htmlFor="rebalanceBand"
            hint="Drift tolerance; omit = no rebalancing."
          >
            <input
              id="rebalanceBand"
              className="input input--num"
              type="number"
              step="0.01"
              min={0}
              {...register('strategy.rebalanceBand', { setValueAs: emptyToUndefinedNumber })}
            />
          </Field>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 'var(--sp-4)' }}>
        <h3 className="card__title">Glide paths</h3>
        <p style={{ color: 'var(--ink-muted)', fontSize: 'var(--fs-small)', marginTop: 0 }}>
          Named allocation schedules. Attach one to an account in the Accounts section by its id.
        </p>
        <GlidePathsEditor form={form} />
      </div>

      <div className="card">
        <h3 className="card__title">Assumptions</h3>
        <DividendYieldsEditor form={form} />
      </div>
    </section>
  );
}

/** Empty/blank → undefined; else the parsed number (keeps optional numeric fields truly optional). */
function emptyToUndefinedNumber(value: unknown): number | undefined {
  if (value === '' || value === null || value === undefined) return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
}
